import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { Send, RefreshCw, Sparkles, MessageCircle } from "lucide-react";

/**
 * /dashboard/ask-mike — "Ask Mike's Mentor"
 *
 * A faith + wellness chat assistant for Faitharama members. Powered by Claude
 * Sonnet 4.6 on the backend with a custom Faitharama system prompt. Stores
 * conversation history server-side per (user, session) so refreshing keeps the
 * thread alive.
 */

const NAVY = "#0F172A";
const GOLD  = "#E8B539";
const WINE  = "#0F172A";
const PAPER = "#FFFFFF";

const STARTERS = [
  "How do I get my mornings off to a better start?",
  "Give me a quick scripture for when I feel anxious.",
  "What's a simple wellness habit for someone over 60?",
  "Help me write a 3-line story for my Mirror Site.",
];

export default function AskMentorPage() {
  const { user } = useAuth();
  const [sid] = useState(() => {
    const k = "faith_chat_sid";
    let s = localStorage.getItem(k);
    if (!s) {
      s = "s_" + Math.random().toString(36).slice(2, 12);
      localStorage.setItem(k, s);
    }
    return s;
  });

  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState("");
  const [sending, setSending]   = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get(`/chat/faith/history/${sid}`)
      .then((r) => setMessages(r.data?.messages || []))
      .catch(() => {});
  }, [user, sid]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  if (user === null) return <Navigate to="/login" replace />;
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const send = async (text) => {
    const msg = (text ?? draft).trim();
    if (!msg || sending) return;
    setDraft("");
    setMessages((m) => [...m, { id: "tmp-" + Date.now(), role: "user", text: msg, ts: new Date().toISOString() }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/faith/message", { session_id: sid, message: msg });
      setMessages((m) => [...m, { id: "r-" + Date.now(), role: "assistant", text: data.reply, ts: new Date().toISOString() }]);
    } catch (e) {
      setMessages((m) => [...m, { id: "e-" + Date.now(), role: "assistant", text: "Hmm — couldn't reach Mike's Mentor right now. Try again in a minute.", ts: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (!confirm("Clear this whole conversation?")) return;
    await api.post(`/chat/faith/reset/${sid}`);
    setMessages([]);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: PAPER }} data-testid="ask-mentor-page">
      <SiteNav />

      <section className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: "#2E9F3A" }}>
              ★ Faith & Wellness Companion
            </div>
            <h1 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight mt-1"
                style={{ color: NAVY }}>
              Ask Mike's Mentor
            </h1>
          </div>
          {messages.length > 0 && (
            <button onClick={reset}
                    data-testid="mentor-reset-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border hover:bg-white"
                    style={{ borderColor: WINE, color: WINE }}>
              <RefreshCw className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>

        <p className="text-sm md:text-base italic mb-4" style={{ color: "#3D3D3D" }}>
          A plain-talkin' helper for faith, wellness, family, and questions about Faitharama. Not a doctor.
          For anything medical, call yours.
        </p>

        {/* Conversation pane */}
        <div ref={scrollRef}
             data-testid="mentor-messages"
             className="bg-white rounded-2xl border-2 p-4 md:p-5 space-y-3 overflow-y-auto"
             style={{ borderColor: "#E5E1D6", minHeight: 420, maxHeight: "65vh" }}>
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3"
                   style={{ background: "#FFFFFF", color: GOLD }}>
                <MessageCircle className="w-7 h-7" />
              </div>
              <div className="font-bold text-lg" style={{ color: NAVY }}>Howdy. Ask me anything.</div>
              <div className="text-sm mt-1" style={{ color: "#5A5A5A" }}>
                Faith, wellness, family, or how Faitharama works.
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-2 max-w-xl mx-auto">
                {STARTERS.map((s, i) => (
                  <button key={i}
                          data-testid={`mentor-starter-${i}`}
                          onClick={() => send(s)}
                          className="text-left text-sm px-3 py-2.5 rounded-lg border-2 hover:shadow-md hover:-translate-y-0.5 transition bg-white"
                          style={{ borderColor: "#E5E1D6", color: "#0F172A" }}>
                    <Sparkles className="inline w-3.5 h-3.5 mr-1.5" style={{ color: GOLD }} />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id}
                 data-testid={`mentor-msg-${m.role}`}
                 className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap shadow-sm`}
                   style={ m.role === "user"
                     ? { background: "#FFFFFF", color: "#FFFFFF" }
                     : { background: "#FFFFFF", color: "#0F172A", border: `1px solid ${GOLD}55` } }>
                {m.text}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="mentor-typing">
              <div className="rounded-2xl px-4 py-2.5 text-sm shadow-sm"
                   style={{ background: "#FFFFFF", border: `1px solid ${GOLD}55`, color: "#5A5A5A" }}>
                <span className="inline-block animate-pulse">Mike's Mentor is thinking…</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form className="mt-3 flex gap-2"
              data-testid="mentor-form"
              onSubmit={(e) => { e.preventDefault(); send(); }}>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type your question…"
            maxLength={4000}
            data-testid="mentor-input"
            disabled={sending}
            className="flex-1 px-4 py-3 rounded-full border-2 text-base outline-none focus:border-[#E8B539]"
            style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }}
          />
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            data-testid="mentor-send-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow hover:scale-[1.03] transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
            <Send className="w-4 h-4" /> Send
          </button>
        </form>

        <div className="text-[11px] mt-3 text-center" style={{ color: "#7A7066" }}>
          Mike's Mentor isn't a doctor, lawyer, or financial advisor. For anything serious, call a professional.
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
